import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, Field } from "@/components/common";

const TagList = ({ items, onChange, testid, placeholder }) => {
  const [v, setV] = useState("");
  const add = () => { const t = v.trim().toUpperCase(); if (t && !items.includes(t)) onChange([...items, t]); setV(""); };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {items.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#0D3B52] text-white px-3 py-1 text-xs font-semibold" data-testid={`${testid}-item-${t}`}>
            {t}<button type="button" onClick={() => onChange(items.filter((x) => x !== t))} aria-label="Rimuovi"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} className="bg-white uppercase" data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" onClick={add} data-testid={`${testid}-add`}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export const AgencySettings = () => {
  const [s, setS] = useState(null);
  const [cnt, setCnt] = useState({ ddt_next: "", ddt_asl_next: "", fattura_next: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => { setS(r.data); setCnt({ ddt_next: r.data.ddt_next, ddt_asl_next: r.data.ddt_asl_next, fattura_next: r.data.fattura_next }); }).catch((e) => toast.error(errMsg(e))); }, []);
  const set = (k) => (e) => setS((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", s);
      await api.post("/admin/settings/counters", { ddt_next: Number(cnt.ddt_next), ddt_asl_next: Number(cnt.ddt_asl_next), fattura_next: Number(cnt.fattura_next) });
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!s) return <Card className="p-6"><Loader2 className="h-5 w-5 animate-spin text-[#0D3B52]" /></Card>;
  return (
    <>
      <Card className="p-6" data-testid="settings-trasportatore">
        <p className="font-display text-2xl font-semibold text-[#0D3B52] mb-1">Dati trasportatore (timbro DDT)</p>
        <p className="text-sm text-[#5C6B73] mb-4">Compaiono nel riquadro Trasportatore e come timbro in fondo a ogni DDT e fattura.</p>
        <div className="space-y-3">
          <Field label="Nome"><Input value={s.trasportatore_nome} onChange={set("trasportatore_nome")} className="bg-white" data-testid="settings-tr-nome" /></Field>
          <Field label="Indirizzo"><Input value={s.trasportatore_indirizzo} onChange={set("trasportatore_indirizzo")} className="bg-white" data-testid="settings-tr-indirizzo" /></Field>
          <Field label="Autorizzazione / N° registrazione"><Input value={s.trasportatore_reg} onChange={set("trasportatore_reg")} className="bg-white" data-testid="settings-tr-reg" /></Field>
          <Field label="Partita IVA"><Input value={s.trasportatore_piva} onChange={set("trasportatore_piva")} className="bg-white" data-testid="settings-tr-piva" /></Field>
          <Field label="Link recensione Google"><Input value={s.review_url} onChange={set("review_url")} className="bg-white" data-testid="settings-review-url" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome impianto cremazione"><Input value={s.impianto_nome || ""} onChange={set("impianto_nome")} className="bg-white" data-testid="settings-impianto-nome" /></Field>
            <Field label="WhatsApp impianto"><Input value={s.impianto_telefono || ""} onChange={set("impianto_telefono")} placeholder="3xx xxx xxxx" className="bg-white" data-testid="settings-impianto-tel" /></Field>
          </div>
        </div>
      </Card>
      <Card className="p-6" data-testid="settings-mezzi">
        <p className="font-display text-2xl font-semibold text-[#0D3B52] mb-4">Targhe, casse e numerazioni</p>
        <div className="space-y-5">
          <Field label="Targhe automezzi"><TagList items={s.targhe} onChange={(v) => setS({ ...s, targhe: v })} testid="settings-targhe" placeholder="es. GA237XM" /></Field>
          <Field label="Numeri casse / contenitori"><TagList items={s.casse} onChange={(v) => setS({ ...s, casse: v })} testid="settings-casse" placeholder="es. RM047" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label={`Prossimo DDT ${s.year}`}><Input type="number" min="1" value={cnt.ddt_next} onChange={(e) => setCnt({ ...cnt, ddt_next: e.target.value })} className="bg-white" data-testid="settings-ddt-next" /></Field>
            <Field label="Prossimo DDT ASL"><Input type="number" min="1" value={cnt.ddt_asl_next} onChange={(e) => setCnt({ ...cnt, ddt_asl_next: e.target.value })} className="bg-white" data-testid="settings-asl-next" /></Field>
            <Field label="Prossima fattura"><Input type="number" min="1" value={cnt.fattura_next} onChange={(e) => setCnt({ ...cnt, fattura_next: e.target.value })} className="bg-white" data-testid="settings-fattura-next" /></Field>
          </div>
          <p className="text-xs text-[#5C6B73]">Le numerazioni proseguono in automatico dal numero che imposti qui.</p>
          <Button onClick={save} disabled={busy} data-testid="settings-save-agency" className="rounded-full bg-[#0D3B52] hover:bg-[#082535]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva impostazioni"}</Button>
        </div>
      </Card>
    </>
  );
};
